import React from "react";
import { MessageSquare, EllipsisIcon, Trash2Icon } from "lucide-react";
import { ChatMetadata } from "../../../data/types/api-types";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { cn } from "../../../lib/utils";
import { ChatType } from "tekkr-common/src/model/chats/types";
import { useListAgentsQuery } from "../../../data/queries/list-agents";
import { getIconComponent } from "../../../components/icon-picker";

interface ChatListItemProps {
    chat: ChatMetadata;
    isActive: boolean;
    onDelete: (chatId: string) => void;
    onSelect: () => void;
}

export function ChatListItem({ chat, isActive, onDelete, onSelect }: ChatListItemProps) {
    const handleDelete = (e: React.MouseEvent) => {
        e.preventDefault();
        if (window.confirm(`Are you sure you want to delete "${chat.title}"? This action cannot be undone.`)) {
            onDelete(chat.id);
        }
    };

    return (
        <div
            onClick={onSelect}
            className={`group flex h-10 cursor-pointer flex-row items-center justify-between rounded-lg p-2 transition-all duration-100 hover:bg-secondary ${isActive && "bg-secondary text-secondary-foreground"}`}
        >
            <div className={cn("flex items-center", chat.title === "New Chat" && "italic text-muted-foreground")}>
                {chat.type === ChatType.Agent ? (
                    <AgentIcon chat={chat as AgentChat} className={"h-4 w-4 shrink-0"} />
                ) : (
                    <MessageSquare className="h-4 w-4 shrink-0" />
                )}
                <div className={"ms-3 flex flex-col items-stretch"}>
                    {chat.type === ChatType.Agent && <AgentDetails chat={chat as AgentChat} />}
                    <div className="line-clamp-1 max-w-[180px] truncate text-sm">{chat.title}</div>
                </div>
            </div>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded p-1 opacity-0 transition-all duration-100 hover:bg-accent active:opacity-100 group-hover:opacity-100"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <EllipsisIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="sr-only">More options</span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 rounded-lg" side="bottom" align="end" sideOffset={6}>
                    <DropdownMenuItem
                        className="cursor-pointer font-medium text-destructive hover:bg-destructive hover:text-white"
                        onClick={handleDelete}
                    >
                        <Trash2Icon className="me-1 h-4 w-4" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

type AgentChat = ChatMetadata<ChatType.Agent>;

function AgentIcon({ chat, className }: { chat: AgentChat; className?: string }) {
    const agents = useListAgentsQuery();
    const agent = agents.data?.find((a) => a.id === chat.context.agentId);

    const Comp = getIconComponent(agent?.icon);
    return <Comp className={className} />;
}

function AgentDetails({ chat }: { chat: AgentChat }) {
    const agents = useListAgentsQuery();
    const agent = agents.data?.find((a) => a.id === chat.context.agentId);

    if (!agent) {
        return null;
    }

    return <div className={"line-clamp-1 text-xs text-muted-foreground"}>{agent.name}</div>;
}
